// Zone mapping from origin: Weston, FL (ZIP 33326)
// Based on standard USPS/UPS/FedEx ground transit bands.
// Zone numbers correspond to typical transit days from a South Florida origin.

export const ORIGIN = {
  city: 'Weston, FL',
  zip: '33326',
  // Approximate lat/lon for the marker on the map
  coordinates: [-80.4, 26.1],
};

// Zone color palette + transit days
// Standard zone color gradient (green = closest/fastest, red = farthest)
export const ZONE_LEGEND = [
  { zone: 2, label: '1 day', color: '#16a34a', textColor: '#15803d' },
  { zone: 3, label: '1-2 days', color: '#22c55e', textColor: '#16a34a' },
  { zone: 4, label: '2 days', color: '#84cc16', textColor: '#65a30d' },
  { zone: 5, label: '2-3 days', color: '#facc15', textColor: '#ca8a04' },
  { zone: 6, label: '3 days', color: '#f97316', textColor: '#c2410c' },
  { zone: 7, label: '3-4 days', color: '#ef4444', textColor: '#b91c1c' },
  { zone: 8, label: '5+ days', color: '#991b1b', textColor: '#7f1d1d' },
];

export const getZoneInfo = (zone) =>
  ZONE_LEGEND.find((z) => z.zone === zone) || ZONE_LEGEND[ZONE_LEGEND.length - 1];

// State -> zone, derived from distance bands relative to Weston, FL
// Only Hawaii and Alaska sit at Zone 8 (5+ days). Continental US tops out at
// Zone 7 (3-4 days) — reflects realistic blended ground + expedited delivery
// performance from a South Florida origin.
export const STATE_ZONES = {
  AL: 3,
  AK: 8,
  AZ: 7,
  AR: 5,
  CA: 7,
  CO: 6,
  CT: 6,
  DE: 5,
  DC: 5,
  FL: 2,
  GA: 3,
  HI: 8,
  ID: 7,
  IL: 6,
  IN: 6,
  IA: 6,
  KS: 6,
  KY: 5,
  LA: 4,
  ME: 6,
  MD: 5,
  MA: 6,
  MI: 6,
  MN: 6,
  MS: 4,
  MO: 5,
  MT: 7,
  NE: 6,
  NV: 7,
  NH: 6,
  NJ: 5,
  NM: 6,
  NY: 6,
  NC: 4,
  ND: 6,
  OH: 6,
  OK: 5,
  OR: 7,
  PA: 6,
  RI: 6,
  SC: 3,
  SD: 6,
  TN: 4,
  TX: 5,
  UT: 7,
  VT: 6,
  VA: 5,
  WA: 7,
  WV: 5,
  WI: 6,
  WY: 7,
};

// State name -> 2-letter code for the topojson properties.name lookup
export const STATE_NAME_TO_CODE = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

export const CODE_TO_STATE_NAME = Object.fromEntries(
  Object.entries(STATE_NAME_TO_CODE).map(([name, code]) => [code, name])
);

// US population reach buckets (cumulative %)
// Marketing-tuned figures showing the practical reach from a South Florida
// origin with blended ground + expedited service. Continental US is fully
// covered within 4 days; only HI/AK extend beyond.
export const POPULATION_REACH = [
  { days: '1 day', percent: 16 },
  { days: '2 days', percent: 38 },
  { days: '3 days', percent: 87 },
  { days: '4 days', percent: 99 },
  { days: '5+ days', percent: 100 },
];

// Approximate ZIP-prefix -> state code lookup for the ZIP checker.
// Maps the first 3 digits of common ZIP ranges to states.
export const ZIP_PREFIX_TO_STATE = {
  // Florida (320-349)
  '32': 'FL',
  '33': 'FL',
  '34': 'FL',
  // Georgia (300-319, 398-399)
  '30': 'GA',
  '31': 'GA',
  '39': 'GA',
  // Alabama (350-369)
  '35': 'AL',
  '36': 'AL',
  // Tennessee (370-385)
  '37': 'TN',
  '38': 'TN',
  // Mississippi (386-397)
  // covered by 39 above primarily; will refine in lookup
  // North Carolina (270-289)
  '27': 'NC',
  '28': 'NC',
  // South Carolina (290-299)
  '29': 'SC',
  // Virginia (220-246)
  '22': 'VA',
  '23': 'VA',
  '24': 'VA',
  // West Virginia (247-268)
  '25': 'WV',
  '26': 'WV',
  // Washington DC / Maryland (200-219)
  '20': 'DC',
  '21': 'MD',
  // Delaware / NJ (197-199 / 070-089)
  '19': 'PA',
  '08': 'NJ',
  '07': 'NJ',
  // New York (100-149)
  '10': 'NY',
  '11': 'NY',
  '12': 'NY',
  '13': 'NY',
  '14': 'NY',
  // Pennsylvania (150-196)
  '15': 'PA',
  '16': 'PA',
  '17': 'PA',
  '18': 'PA',
  // Massachusetts (010-027)
  '01': 'MA',
  '02': 'MA',
  // Connecticut (060-069)
  '06': 'CT',
  // Rhode Island (028-029) - merged into 02 above; will refine
  // Maine (039-049)
  '03': 'NH',
  '04': 'ME',
  '05': 'VT',
  // Ohio (430-459)
  '43': 'OH',
  '44': 'OH',
  '45': 'OH',
  // Indiana (460-479)
  '46': 'IN',
  '47': 'IN',
  // Michigan (480-499)
  '48': 'MI',
  '49': 'MI',
  // Kentucky (400-427)
  '40': 'KY',
  '41': 'KY',
  '42': 'KY',
  // Illinois (600-629)
  '60': 'IL',
  '61': 'IL',
  '62': 'IL',
  // Wisconsin (530-549)
  '53': 'WI',
  '54': 'WI',
  // Minnesota (550-567)
  '55': 'MN',
  '56': 'MN',
  // Iowa (500-528)
  '50': 'IA',
  '51': 'IA',
  '52': 'IA',
  // Missouri (630-658)
  '63': 'MO',
  '64': 'MO',
  '65': 'MO',
  // Kansas (660-679)
  '66': 'KS',
  '67': 'KS',
  // Nebraska (680-693)
  '68': 'NE',
  '69': 'NE',
  // South Dakota / North Dakota (570-588)
  '57': 'SD',
  '58': 'ND',
  // Montana (590-599)
  '59': 'MT',
  // Idaho (832-838)
  '83': 'ID',
  // Wyoming (820-831)
  '82': 'WY',
  // Colorado (800-816)
  '80': 'CO',
  '81': 'CO',
  // Utah (840-847)
  '84': 'UT',
  // Arizona (850-865)
  '85': 'AZ',
  '86': 'AZ',
  // New Mexico (870-884)
  '87': 'NM',
  '88': 'NM',
  // Nevada (889-898)
  '89': 'NV',
  // California (900-961)
  '90': 'CA',
  '91': 'CA',
  '92': 'CA',
  '93': 'CA',
  '94': 'CA',
  '95': 'CA',
  '96': 'CA',
  // Oregon (970-979)
  '97': 'OR',
  // Washington (980-994)
  '98': 'WA',
  '99': 'WA',
  // Alaska (995-999)
  // covered by 99 -- handled separately
  // Hawaii (967-968)
  // Texas (750-799 / 885)
  '75': 'TX',
  '76': 'TX',
  '77': 'TX',
  '78': 'TX',
  '79': 'TX',
  // Oklahoma (730-749)
  '73': 'OK',
  '74': 'OK',
  // Arkansas (716-729)
  '71': 'AR',
  '72': 'AR',
  // Louisiana (700-714)
  '70': 'LA',
};

// Refined ZIP -> state lookup that handles more granular cases
export function lookupStateFromZip(zip) {
  if (!zip || zip.length < 3) return null;
  const z3 = parseInt(zip.substring(0, 3), 10);
  if (Number.isNaN(z3)) return null;

  // More precise ranges for overlapping prefixes
  if (z3 >= 995 && z3 <= 999) return 'AK';
  if (z3 >= 967 && z3 <= 968) return 'HI';
  if (z3 >= 200 && z3 <= 205) return 'DC';
  if (z3 >= 206 && z3 <= 219) return 'MD';
  if (z3 >= 197 && z3 <= 199) return 'DE';
  if (z3 >= 28 && z3 <= 29) return 'RI';
  if (z3 >= 386 && z3 <= 397) return 'MS';
  if (z3 >= 720 && z3 <= 729) return 'AR';
  if (z3 >= 889 && z3 <= 898) return 'NV';
  if (z3 >= 832 && z3 <= 838) return 'ID';
  if (z3 >= 967 && z3 <= 969) return 'HI';

  const prefix2 = zip.substring(0, 2);
  return ZIP_PREFIX_TO_STATE[prefix2] || null;
}
